import React, { forwardRef, useContext, useImperativeHandle } from "react";
import { Select } from "@mantine/core";
import { Validatable } from "./StudyStepper";
import { notifications } from "@mantine/notifications";
import { UserContext } from "@/contexts/UserContext/UserContext";
import { getFormStyles } from "@/helpers/getFormStyles";
import { useDarkModeContext } from "@/hooks/contextHooks/useDarkModeContext";
import { ShipDataType } from "@/types/apiTypes";
import { Text } from "@/components/Generics/Text";
import styles from "../../css/study.module.css";

type Props = {
  ships: ShipDataType[];
};

const ChooseLockedShipsStep = forwardRef<Validatable, Props>(
  ({ ships }, ref) => {
    const context = useContext(UserContext);

    const { isDarkMode } = useDarkModeContext();
    const formStyles = getFormStyles(isDarkMode);

    const handleChange = (selectedShipId: string | null) => {
      if (selectedShipId) {
        const selectedShip = ships.find((ship) => ship.id === selectedShipId);
        if (selectedShip) {
          context?.setStudyFormValues({
            ...context.studyFormValues,
            lockedShips: [selectedShipId],
            vesselData: selectedShip.vessel_data, // Store the vessel data in the context
          });
        }
      } else {
        context?.setStudyFormValues({
          ...context.studyFormValues,
          lockedShips: [],
          vesselData: null, // Clear the vessel data if no ship is selected
        });
      }
    };

    // Filter the ships to only include locked ones
    const lockedShips = ships.filter((ship) => ship.locked);

    const validate = () => {
      if (
        !context?.studyFormValues.lockedShips ||
        context.studyFormValues.lockedShips.length === 0
      ) {
        notifications.show({
          title: "Validation Error",
          message: "Please select at least one locked ship.",
          color: "red",
          autoClose: 4000,
          position: "top-center",
        });
        return false;
      }
      return true;
    };

    useImperativeHandle(ref, () => ({
      validate,
    }));

    return (
      <div className={styles.stepContainer}>
        <Select
          label="Choose a Ship"
          data={lockedShips.map((ship) => ({
            value: ship.id,
            label: ship.ship_name,
          }))}
          value={context?.studyFormValues.lockedShips?.[0] || ""}
          onChange={handleChange}
          placeholder={
            lockedShips.length > 0
              ? "Select a locked ship"
              : "No locked ships available"
          }
          disabled={lockedShips.length === 0}
          clearable
          styles={formStyles}
        />
        {lockedShips.length === 0 && (
          <Text color="red">
            You have no locked ships. Please lock a ship before proceeding.
          </Text>
        )}
      </div>
    );
  },
);

export default ChooseLockedShipsStep;
